import { storageManager } from '../../../core/storage/index.js';
import { storage } from '../../../js/utils/storage.js';
import { resolveUserAvatarSrc } from '../../utils/userAvatar.js';
import { HangoutState } from '../state/HangoutState.js';
import { fetchCharacterSpriteUrlStrict } from './character-manager.js';
import { setFormattedContent } from './text-formatting.js';
import { handleVoiceButtonClick, syncMessageAudioCacheAfterEdit } from './voice-utils.js';
import { getScenarioDescriptionFromHangoutConfig, getScenarioNameFromHangoutConfig } from '../shared/scenario-config.js';
import { resolveTextPlaceholders, resolveUserNameFromStorage } from '../../utils/placeholder-resolver.js';
import { resolveMessageSpriteData } from '../shared/sprite-state.js';
import { attachSpriteContextMenuListeners } from './SpriteContextMenu.js';
import { parseSpriteUrl } from './services/spriteUpgradeService.js';
import {
    buildVersionMetaIndex,
    getVersionMetaFromIndex,
    getVersionMetaForPath,
    getPrecedingUserMessage,
    greetingChainPathAtIndex,
    normalizeMessagePath,
    isGreetingMessage,
    resolveGreetingChainPath
} from '../shared/message-tree.js';
import { getTranscriptForLayout } from '../shared/history-resolution.js';
import { isMessageSendInFlight } from './message-send-state.js';

const loadMessageTreeActions = () => import('./message-utils.js');

const resolveRetryUserPath = (messageRecord) => {
    const hangoutState = HangoutState.getInstance();
    const messages = hangoutState.getMessages?.() || [];
    const recordPath = normalizeMessagePath(messageRecord?.path);
    if (recordPath) {
        const precedingPath = normalizeMessagePath(getPrecedingUserMessage(messages, recordPath)?.path);
        if (precedingPath) return precedingPath;
    }
    const visible = getTranscriptForLayout(hangoutState);
    for (let index = visible.length - 1; index >= 0; index -= 1) {
        if (visible[index]?.role !== 'user') continue;
        return normalizeMessagePath(visible[index]?.path) || null;
    }
    return null;
};

const transcriptEndsOnUserPath = (messagePath) => {
    const visible = getTranscriptForLayout(HangoutState.getInstance());
    const last = visible[visible.length - 1];
    if (last?.role !== 'user') return false;
    return normalizeMessagePath(last.path) === normalizeMessagePath(messagePath);
};

const isGreetingVariationNavHost = (messageRecord) => {
    if (!messageRecord?.isGreeting) return false;
    const hangoutState = HangoutState.getInstance();
    const variation = hangoutState.getGreetingVariationMeta?.().variation ?? 0;
    const hostPath = greetingChainPathAtIndex(0, variation);
    const recordPath = normalizeMessagePath(messageRecord.path);
    if (recordPath) return recordPath === hostPath;
    const roster = hangoutState.getCharacters?.() || hangoutState._getConfig?.()?.allCharacters || [];
    if (Array.isArray(roster) && roster.length > 0) {
        return resolveGreetingChainPath(messageRecord.characterId, roster, variation) === hostPath;
    }
    const firstGreeting = (hangoutState.getVisibleMessages?.() || hangoutState.getMessages?.() || [])
        .find((message) => isGreetingMessage(message));
    if (!firstGreeting) return true;
    return String(messageRecord.characterId) === String(firstGreeting.characterId ?? firstGreeting.character_id ?? '');
};

const UNKNOWN_AVATAR_URL = '/assets/images/characters/unknown.png';
const JOIN_MESSAGE_STANDING_ACTION_ID = 2016;

const isRosterNoticeMessage = (message) =>
    message?.isJoinMessage === true || message?.isLeaveMessage === true;

const resolveChatbotAvatarActionId = (message, resolvedActionId) => {
    if (resolvedActionId !== null && resolvedActionId !== undefined) return resolvedActionId;
    if (isRosterNoticeMessage(message)) return JOIN_MESSAGE_STANDING_ACTION_ID;
    return null;
};

let deferChatAvatarVideoUpgrade = false;

export const setDeferChatAvatarVideoUpgrade = (value) => {
    deferChatAvatarVideoUpgrade = Boolean(value);
};

export const upgradeDeferredChatAvatarsToVideo = async () => {
    if (!chatbotDisplayManagerInstance?.initialized) return;
    await chatbotDisplayManagerInstance.upgradeChatAvatarsToVideo();
};

const resolveCharacterMetaMap = async () => {
    try {
        const config = await storageManager.getItem('currentHangoutConfig');
        const entries = Array.isArray(config?.allCharacters) ? config.allCharacters : [];
        const map = new Map();
        for (const character of entries) {
            const id = String(character?.id ?? character?.characterId ?? '');
            if (!id) continue;
            map.set(id, character);
        }
        return map;
    } catch {
        return new Map();
    }
};

const resolveAvatarUrl = (character = {}) =>
    character.avatar
    || character.avatar_url
    || character.image
    || character.image_url
    || character.modalImage
    || character.modal_image
    || character.spriteUrl
    || character.sprite_url
    || UNKNOWN_AVATAR_URL;

const sanitizeDisplayName = (value) => {
    if (typeof value !== 'string') return null;
    const trimmed = value.trim();
    if (!trimmed) return null;
    return trimmed;
};

const isPlaceholderDisplayName = (value, characterId = null) => {
    const sanitizedName = sanitizeDisplayName(value);
    if (!sanitizedName) return true;

    const lowerName = sanitizedName.toLowerCase();
    if (lowerName === 'character' || lowerName === 'unknown') return true;
    if (/^character\s+\d+$/i.test(sanitizedName)) return true;
    if (/^\d+$/.test(sanitizedName)) return true;

    if (characterId !== null && characterId !== undefined) {
        const normalizedCharacterId = String(characterId).trim();
        if (normalizedCharacterId && sanitizedName === normalizedCharacterId) return true;
    }

    return false;
};

const isValidAvatarUrl = (value) => {
    if (typeof value !== 'string') return false;
    const url = value.trim();
    if (!url) return false;
    if (url.startsWith('http://') || url.startsWith('https://')) return true;
    if (url.startsWith('/') && !url.startsWith('//')) return true;
    return false;
};

const resolveUserAvatarUrl = () => resolveUserAvatarSrc(storage.get('userData') || {});

const isVideoAvatarUrl = (url) => typeof url === 'string' && /\.webm(\?|$)/i.test(url);

const resolveUserDisplayName = () => {
    const userData = storage.get('userData') || {};
    return sanitizeDisplayName(userData.name)
        || sanitizeDisplayName(userData.username)
        || sanitizeDisplayName(userData.display_name)
        || sanitizeDisplayName(userData.displayName)
        || 'You';
};

const resolveScenarioHeader = () => {
    const config = storage.get('currentHangoutConfig') || {};
    const configuredCharacters = Array.isArray(config.allCharacters) ? config.allCharacters : [];
    const primaryCharacterName = sanitizeDisplayName(configuredCharacters[0]?.name)
        || sanitizeDisplayName(config.selectedCharacter?.name)
        || 'Character';
    const scenarioUserName = resolveUserNameFromStorage({ fallback: 'You' });
    const placeholderOptions = {
        userName: scenarioUserName,
        scenarioChars: configuredCharacters,
        fallbackCharName: primaryCharacterName || 'Character',
        preserveUserTokenWhenMissing: false
    };
    const scenarioName = resolveTextPlaceholders(
        sanitizeDisplayName(getScenarioNameFromHangoutConfig(config)) || '',
        placeholderOptions
    );
    const rawScenarioDescription = sanitizeDisplayName(getScenarioDescriptionFromHangoutConfig(config));
    const scenarioDescription = rawScenarioDescription
        ? resolveTextPlaceholders(
            rawScenarioDescription.replace(/\\/g, ''),
            placeholderOptions
        )
        : null;
    return { scenarioName, scenarioDescription };
};

export class ChatbotDisplayManager {
    constructor() {
        this.initialized = false;
        this.container = null;
        this.messagesContainer = null;
        this.dimLayer = null;
        this.activeMessages = [];
        this.lastDisplayIndex = 0;
        this.characterMetaMap = new Map();
        this._avatarFocusClickHandler = null;
        this._avatarFocusKeyHandler = null;
        this._avatarContextMenuBound = false;
        this._hydratedSignature = null;
    }

    async initialize() {
        if (this.initialized) return this;
        if (typeof document === 'undefined') return this;

        this.container = document.querySelector('.chat-container');
        if (!this.container) {
            console.warn('ChatbotDisplayManager: .chat-container not found');
            return this;
        }

        this.messagesContainer = this.container.querySelector('.chat-messages');
        if (!this.messagesContainer) {
            this.messagesContainer = document.createElement('div');
            this.messagesContainer.className = 'chat-messages';
            this.container.appendChild(this.messagesContainer);
        }

        this.messagesContainer.classList.add('chatbot-transcript');
        this._ensureDimLayer();
        this._setupAvatarFocusDelegation();
        this._setupAvatarContextMenuDelegation();
        this.characterMetaMap = await resolveCharacterMetaMap();
        this.initialized = true;
        return this;
    }

    _setupAvatarFocusDelegation() {
        if (!this.messagesContainer) return;
        if (this._avatarFocusClickHandler) return;

        this._avatarFocusClickHandler = (event) => {
            const avatar = event.target?.closest?.('.chatbot-message-avatar');
            if (!avatar || !this.messagesContainer.contains(avatar)) return;
            void this._handleAvatarFocusActivate(avatar);
        };

        this._avatarFocusKeyHandler = (event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            const avatar = event.target?.closest?.('.chatbot-message-avatar');
            if (!avatar || !this.messagesContainer.contains(avatar)) return;
            event.preventDefault();
            void this._handleAvatarFocusActivate(avatar);
        };

        this.messagesContainer.addEventListener('click', this._avatarFocusClickHandler);
        this.messagesContainer.addEventListener('keydown', this._avatarFocusKeyHandler);
    }

    _setupAvatarContextMenuDelegation() {
        if (!this.messagesContainer || this._avatarContextMenuBound) return;
        this._avatarContextMenuBound = true;
        attachSpriteContextMenuListeners(this.messagesContainer, {
            selector: '.chatbot-message-avatar',
            getOverrideSpriteData: (avatarEl) => this._resolveAssistantAvatarSpriteData(avatarEl)
        });
    }

    _resolveAssistantAvatarSpriteData(avatarEl) {
        const row = avatarEl?.closest?.('.chatbot-message-row');
        if (!row || row.dataset.role === 'user') return false;

        const messageRecord = this.activeMessages.find((message) => message?.element === row) || null;
        const mediaUrl = avatarEl?.currentSrc || avatarEl?.src || '';
        const parsed = parseSpriteUrl(mediaUrl);
        return {
            characterId: messageRecord?.characterId || row.dataset.characterId || parsed.characterId,
            outfitId: messageRecord?.spriteMeta?.outfitId ?? messageRecord?.spriteMeta?.outfit_id ?? parsed.outfitId ?? 0,
            actionId: messageRecord?.spriteMeta?.actionId ?? messageRecord?.spriteMeta?.action_id ?? parsed.actionId ?? null,
            videoActionId: parsed.videoActionId,
            spriteUrl: mediaUrl,
            path: messageRecord?.path || row.dataset.path || null,
            isVideo: messageRecord?.spriteMeta?.isVideo === true || avatarEl?.tagName === 'VIDEO',
            actionName: messageRecord?.spriteMeta?.actionName || '',
            source: 'chatbot-message-avatar'
        };
    }

    async _handleAvatarFocusActivate(avatarEl) {
        const row = avatarEl.closest('.chatbot-message-row');
        if (!row || row.dataset.role === 'user') return;

        const characterId = row.dataset.characterId;
        if (!characterId || characterId === 'user') return;

        const messageRecord = this.activeMessages.find((message) => message?.element === row) || null;
        const path = messageRecord?.path || row.dataset.path || null;
        const hangoutState = HangoutState.getInstance();
        const history = hangoutState?.getMessages?.() || [];
        const normalizedPath = normalizeMessagePath(path);
        const matchedMessage = normalizedPath
            ? history.find((message) => normalizeMessagePath(message?.path) === normalizedPath)
            : null;
        const locationRaw = matchedMessage?.locationId
            ?? matchedMessage?.location_id
            ?? messageRecord?.locationId
            ?? messageRecord?.spriteMeta?.locationId;
        const locationParsed = Number.parseInt(String(locationRaw ?? ''), 10);
        const spriteOptions = {
            ...(messageRecord?.spriteMeta || {}),
            ...(Number.isFinite(locationParsed) && locationParsed >= 0 ? { locationId: locationParsed } : {}),
            ...(matchedMessage?.actionName ? { actionName: matchedMessage.actionName } : {})
        };

        try {
            const { focusHangoutConversationTurn } = await loadMessageTreeActions();
            if (typeof focusHangoutConversationTurn === 'function') {
                await focusHangoutConversationTurn({ path, characterId, spriteOptions });
            }
        } catch (error) {
            console.warn('ChatbotDisplayManager: avatar sprite focus failed', error);
        }
    }

    _prepareAssistantAvatarInteractive(avatarEl, characterName) {
        if (!avatarEl) return;
        avatarEl.setAttribute('tabindex', '0');
        avatarEl.setAttribute('role', 'button');
        avatarEl.setAttribute(
            'aria-label',
            `Show ${characterName || 'character'} sprite and scene`
        );
        avatarEl.style.cursor = 'pointer';
    }

    _ensureDimLayer() {
        if (this.dimLayer || typeof document === 'undefined') return;
        const hangoutContainer = document.getElementById('hangout-container');
        if (!hangoutContainer) return;
        this.dimLayer = document.createElement('div');
        this.dimLayer.className = 'chatbot-dim-layer';
        this.dimLayer.setAttribute('aria-hidden', 'true');
        hangoutContainer.appendChild(this.dimLayer);
    }

    isAvailable() {
        return !!this.messagesContainer;
    }

    setTurnNavigation() {
        // Chatbot mode does not use VN turn navigation.
    }

    _findLatestMessageByCharacterId(characterId) {
        const normalizedId = String(characterId);
        for (let i = this.activeMessages.length - 1; i >= 0; i -= 1) {
            const message = this.activeMessages[i];
            if (!message || message.role === 'user' || message.role === 'system') continue;
            if (String(message.characterId) !== normalizedId) continue;
            return message;
        }
        return null;
    }

    _findLatestTypingMessageByCharacterId(characterId) {
        const normalizedId = String(characterId);
        for (let i = this.activeMessages.length - 1; i >= 0; i -= 1) {
            const message = this.activeMessages[i];
            if (String(message.characterId) !== normalizedId) continue;
            if (message.isTyping) return message;
        }
        return null;
    }

    _findMessageByStreamGeneration(characterId, streamGenerationId) {
        const generationId = String(streamGenerationId || '');
        if (!generationId) return null;
        const normalizedId = String(characterId);
        for (let i = this.activeMessages.length - 1; i >= 0; i -= 1) {
            const message = this.activeMessages[i];
            if (!message || message.role === 'user') continue;
            if (String(message.characterId) !== normalizedId) continue;
            if (String(message.streamGenerationId || '') !== generationId) continue;
            return message;
        }
        return null;
    }

    _resolveStreamTargetMessage(characterId, options = {}) {
        const generationId = options.streamGenerationId;
        if (generationId) {
            return this._findMessageByStreamGeneration(characterId, generationId);
        }
        return this._findLatestTypingMessageByCharacterId(characterId)
            || this._findLatestMessageByCharacterId(characterId);
    }

    _findMessageRecordByPath(path, characterId = null) {
        const normalizedPath = normalizeMessagePath(path);
        if (!normalizedPath) return null;
        const normalizedCharacterId = characterId != null ? String(characterId) : null;
        for (let i = this.activeMessages.length - 1; i >= 0; i -= 1) {
            const message = this.activeMessages[i];
            if (!message || message.role === 'user') continue;
            if (normalizedCharacterId != null && String(message.characterId) !== normalizedCharacterId) continue;
            const messagePath = normalizeMessagePath(message.path)
                || normalizeMessagePath(message.element?.dataset?.path);
            if (messagePath === normalizedPath) return message;
        }
        return null;
    }

    bindPathToLatestAssistantMessage(characterId, path, options = {}) {
        const normalizedPath = normalizeMessagePath(path);
        if (!normalizedPath) return null;
        const message = this._resolveStreamTargetMessage(characterId, options);
        if (!message || message.role === 'user') return null;

        message.path = normalizedPath;
        if (message.element) message.element.dataset.path = normalizedPath;

        const hangoutState = HangoutState.getInstance();
        const versionMeta = getVersionMetaForPath(
            hangoutState?.getMessages?.() || [],
            normalizedPath,
            characterId,
            hangoutState?.getActiveVersions?.() || {}
        );
        message.canonicalPath = options.canonicalPath
            ? String(options.canonicalPath)
            : (versionMeta.canonicalPath || message.canonicalPath || null);
        message.versionIndex = options.versionIndex || versionMeta.index || message.versionIndex || 1;
        message.versionTotal = options.versionTotal || versionMeta.total || message.versionTotal || 1;
        message._renderVersionControls?.();
        return message;
    }

    resolvePathForMessageRecord(messageRecord) {
        if (!messageRecord) return null;
        const existingPath = normalizeMessagePath(messageRecord.path);
        if (existingPath) return existingPath;

        const characterId = String(messageRecord.characterId ?? '');
        if (!characterId) return null;
        const content = String(messageRecord.text || '').trim();
        const hangoutState = HangoutState.getInstance();
        const messages = Array.isArray(hangoutState?.getMessages?.()) ? hangoutState.getMessages() : [];

        for (let index = messages.length - 1; index >= 0; index -= 1) {
            const message = messages[index];
            if (message?.role !== 'assistant') continue;
            if (String(message.characterId ?? '') !== characterId) continue;
            if (content && String(message.content || '').trim() !== content) continue;
            const matchedPath = normalizeMessagePath(message.path);
            if (matchedPath) return matchedPath;
        }

        for (let index = messages.length - 1; index >= 0; index -= 1) {
            const message = messages[index];
            if (message?.role !== 'assistant') continue;
            if (String(message.characterId ?? '') !== characterId) continue;
            const tipPath = normalizeMessagePath(message.path);
            if (tipPath) return tipPath;
        }

        return null;
    }

    _resolveAssistantCharacterName(characterId, candidateNames = []) {
        const normalizedCharacterId = String(characterId ?? '');
        for (const candidate of candidateNames) {
            const sanitizedCandidate = sanitizeDisplayName(candidate);
            if (isPlaceholderDisplayName(sanitizedCandidate, normalizedCharacterId)) continue;
            return sanitizedCandidate;
        }

        const characterMeta = this.characterMetaMap.get(normalizedCharacterId);
        const metaCandidates = [
            characterMeta?.name,
            characterMeta?.characterName,
            characterMeta?.display_name,
            characterMeta?.displayName
        ];
        for (const metaCandidate of metaCandidates) {
            const sanitizedMetaName = sanitizeDisplayName(metaCandidate);
            if (isPlaceholderDisplayName(sanitizedMetaName, normalizedCharacterId)) continue;
            return sanitizedMetaName;
        }

        return 'Character';
    }

    _buildMessageRecord(characterId, characterName, options = {}) {
        const normalizedCharacterId = String(characterId ?? '');
        const isRosterNotice = options.role === 'system' || options.isRosterNotice === true;
        const role = options.role === 'user' ? 'user' : (isRosterNotice ? 'system' : 'assistant');
        const isGreeting = options.isGreeting === true
            || options.isInitialGreeting === true
            || isGreetingMessage({ path: options.path, isGreeting: options.isGreeting, isInitialGreeting: options.isInitialGreeting });
        const messageEl = document.createElement('article');
        messageEl.className = isRosterNotice
            ? 'chatbot-message-row chatbot-message-row-assistant chatbot-message-row-system'
            : `chatbot-message-row chatbot-message-row-${role}`;
        messageEl.dataset.characterId = normalizedCharacterId;
        messageEl.dataset.role = role;
        if (options.path) messageEl.dataset.path = String(options.path);

        const avatarEl = document.createElement('img');
        avatarEl.className = 'chatbot-message-avatar';
        avatarEl.alt = `${characterName || 'Character'} avatar`;
        avatarEl.loading = 'lazy';
        if (role === 'assistant' || isRosterNotice) {
            this._prepareAssistantAvatarInteractive(avatarEl, characterName);
        }

        const contentEl = document.createElement('div');
        contentEl.className = 'chatbot-message-content';

        const headerEl = document.createElement('div');
        headerEl.className = 'chatbot-message-header';

        const nameEl = document.createElement('span');
        nameEl.className = 'chatbot-message-name';
        nameEl.textContent = characterName || (role === 'user' ? resolveUserDisplayName() : 'Character');

        const voiceButton = document.createElement('button');
        voiceButton.type = 'button';
        voiceButton.className = 'chatbot-message-voice-btn';
        voiceButton.setAttribute('aria-label', 'Play voice line');
        voiceButton.setAttribute('title', 'Play voice line');
        voiceButton.innerHTML = '<i class="fas fa-volume-up" aria-hidden="true"></i>';

        const textEl = document.createElement('div');
        textEl.className = 'chatbot-message-text';

        const emptyRetryButton = document.createElement('button');
        emptyRetryButton.type = 'button';
        emptyRetryButton.className = 'chatbot-message-action-btn chatbot-message-retry-btn';
        emptyRetryButton.setAttribute('aria-label', 'Retry reply');
        emptyRetryButton.setAttribute('title', 'Retry reply');
        emptyRetryButton.innerHTML = '<i class="fas fa-sync" aria-hidden="true"></i>';
        emptyRetryButton.hidden = true;

        headerEl.appendChild(nameEl);
        if (role === 'assistant' && !isRosterNotice) {
            headerEl.appendChild(voiceButton);
        } else {
            voiceButton.style.display = 'none';
        }

        contentEl.appendChild(headerEl);
        contentEl.appendChild(textEl);

        let footerEl = null;
        let editButton = null;
        let leftArrowButton = null;
        let rightArrowButton = null;
        let continueButton = null;
        let versionCountEl = null;
        let userRetryButton = null;
        if (!isRosterNotice) {
        footerEl = document.createElement('div');
        footerEl.className = 'chatbot-message-footer';

        editButton = document.createElement('button');
        editButton.type = 'button';
        editButton.className = 'chatbot-message-action-btn chatbot-message-edit-btn';
        editButton.setAttribute('aria-label', 'Edit message');
        editButton.setAttribute('title', 'Edit message');
        editButton.innerHTML = '<i class="fas fa-pen" aria-hidden="true"></i>';
        footerEl.appendChild(editButton);

        if (role === 'user') {
            userRetryButton = document.createElement('button');
            userRetryButton.type = 'button';
            userRetryButton.className = 'chatbot-message-action-btn chatbot-message-retry-btn';
            userRetryButton.setAttribute('aria-label', 'Retry message');
            userRetryButton.setAttribute('title', 'Retry message');
            userRetryButton.innerHTML = '<i class="fas fa-sync" aria-hidden="true"></i>';
            userRetryButton.hidden = true;
            footerEl.appendChild(userRetryButton);
        }

        if (role === 'assistant') {
            leftArrowButton = document.createElement('button');
            leftArrowButton.type = 'button';
            leftArrowButton.className = 'chatbot-message-action-btn chatbot-message-version-btn';
            leftArrowButton.setAttribute('aria-label', isGreeting ? 'Previous greeting variation' : 'Previous message version');
            leftArrowButton.setAttribute('title', isGreeting ? 'Previous greeting variation' : 'Previous message version');
            leftArrowButton.innerHTML = '<i class="fas fa-chevron-left" aria-hidden="true"></i>';

            versionCountEl = document.createElement('span');
            versionCountEl.className = 'chatbot-message-version-count';
            versionCountEl.hidden = true;

            rightArrowButton = document.createElement('button');
            rightArrowButton.type = 'button';
            rightArrowButton.className = 'chatbot-message-action-btn chatbot-message-version-btn';
            rightArrowButton.setAttribute('aria-label', isGreeting ? 'Next greeting variation' : 'Next message version');
            rightArrowButton.setAttribute('title', isGreeting ? 'Next greeting variation' : 'Next message version');
            rightArrowButton.innerHTML = '<i class="fas fa-chevron-right" aria-hidden="true"></i>';

            footerEl.appendChild(leftArrowButton);
            footerEl.appendChild(versionCountEl);
            footerEl.appendChild(rightArrowButton);

            continueButton = document.createElement('button');
            continueButton.type = 'button';
            continueButton.className = 'chatbot-message-action-btn chatbot-message-continue-btn';
            continueButton.setAttribute('aria-label', 'Continue character message');
            continueButton.setAttribute('title', 'Continue character message');
            continueButton.innerHTML = '<i class="fas fa-angle-double-right" aria-hidden="true"></i>';
            continueButton.hidden = true;
            footerEl.appendChild(continueButton);
        }
        contentEl.appendChild(footerEl);
        }

        messageEl.appendChild(avatarEl);
        messageEl.appendChild(contentEl);

        const mountTarget = options.mountTarget || this.messagesContainer;
        if (mountTarget) {
            mountTarget.appendChild(messageEl);
        }

        const messageRecord = {
            characterId: normalizedCharacterId,
            characterName: characterName || 'Character',
            text: '',
            path: options.path ? String(options.path) : null,
            canonicalPath: options.canonicalPath ? String(options.canonicalPath) : null,
            isGreeting,
            isTyping: false,
            isAwaitingDecision: false,
            isReasoningPreview: false,
            streamGenerationId: options.streamGenerationId ? String(options.streamGenerationId) : null,
            isEditing: false,
            voiceButtonState: 'play',
            isAudioPlaying: false,
            currentVoiceKey: null,
            spriteMeta: options.spriteMeta || null,
            avatarFallbackUrl: options.avatarFallbackUrl || null,
            role,
            versionIndex: options.versionIndex || 1,
            versionTotal: options.versionTotal || 1,
            element: messageEl,
            avatarElement: avatarEl,
            textElement: textEl,
            voiceButtonElement: voiceButton,
            footerElement: footerEl,
            editButtonElement: editButton,
            leftArrowButtonElement: leftArrowButton,
            rightArrowButtonElement: rightArrowButton,
            continueButtonElement: continueButton,
            versionCountElement: versionCountEl,
            querySelector: (selector) => messageEl.querySelector(selector),
            setText: (text) => {
                messageRecord.text = text || '';
                if (messageRecord.isEditing) return;
                if (messageRecord.isTyping && (messageRecord.text === '' || messageRecord.text === '...')) {
                    textEl.innerHTML = '<div class="typing-dots vn-textbox-typing-dots" aria-hidden="true"><span></span><span></span><span></span></div>';
                    textEl.setAttribute('aria-busy', 'true');
                    emptyRetryButton.hidden = true;
                    return;
                }
                textEl.removeAttribute('aria-busy');
                const showEmptyRetry = role === 'assistant'
                    && !isRosterNotice
                    && !messageRecord.isReasoningPreview
                    && !messageRecord.isGreeting
                    && !String(messageRecord.text || '').trim();
                if (showEmptyRetry) {
                    textEl.replaceChildren(emptyRetryButton);
                    emptyRetryButton.hidden = false;
                    emptyRetryButton.disabled = isMessageSendInFlight();
                    return;
                }
                emptyRetryButton.hidden = true;
                setFormattedContent(textEl, messageRecord.text);
            },
            setReasoningPreview: (enabled) => {
                messageRecord.isReasoningPreview = enabled === true;
                textEl.classList.toggle('is-reasoning', messageRecord.isReasoningPreview);
            },
            _syncEmptyRetry: () => {
                if (role !== 'assistant' || isRosterNotice) return;
                const showEmptyRetry = !messageRecord.isTyping
                    && !messageRecord.isEditing
                    && !messageRecord.isReasoningPreview
                    && !messageRecord.isGreeting
                    && !String(messageRecord.text || '').trim();
                if (!showEmptyRetry) {
                    emptyRetryButton.hidden = true;
                    return;
                }
                if (!textEl.contains(emptyRetryButton)) {
                    textEl.replaceChildren(emptyRetryButton);
                }
                emptyRetryButton.hidden = false;
                emptyRetryButton.disabled = isMessageSendInFlight();
            },
            markDirty: () => {
                messageRecord._syncEmptyRetry();
                messageRecord._renderVoiceButtonState();
                messageRecord._renderVersionControls();
            },
            setVoiceButtonState: (state) => {
                messageRecord.voiceButtonState = state;
                messageRecord._renderVoiceButtonState();
            },
            _renderVoiceButtonState: () => {
                if (role !== 'assistant') return;
                const state = messageRecord.voiceButtonState || 'play';
                let label = 'Play voice line';
                let icon = '<i class="fas fa-volume-up" aria-hidden="true"></i>';
                if (state === 'playing') {
                    label = 'Pause voice line';
                    icon = '<i class="fas fa-pause" aria-hidden="true"></i>';
                } else if (state === 'loading') {
                    label = 'Voice loading';
                    icon = '<i class="fas fa-spinner fa-spin" aria-hidden="true"></i>';
                } else if (state === 'error') {
                    label = 'Voice failed';
                    icon = '<i class="fas fa-exclamation-triangle" aria-hidden="true"></i>';
                } else if (state === 'ready' || state === 'pause') {
                    label = 'Play voice line';
                    icon = '<i class="fas fa-play" aria-hidden="true"></i>';
                }
                voiceButton.setAttribute('aria-label', label);
                voiceButton.setAttribute('title', label);
                voiceButton.innerHTML = icon;
                voiceButton.classList.toggle('chatbot-message-voice-btn-ready', state === 'ready' || state === 'playing' || state === 'pause');
            },
            _renderVersionControls: () => {
                if (!footerEl) return;
                if (editButton) {
                    editButton.disabled = messageRecord.isTyping
                        || messageRecord.isAwaitingDecision
                        || messageRecord.isReasoningPreview;
                }
                if (role === 'user' && userRetryButton) {
                    const turnBusy = isMessageSendInFlight()
                        || (this.activeMessages || []).some((entry) => entry?.isTyping === true);
                    const emptyAssistantRetry = (this.activeMessages || []).some((entry) =>
                        entry?.role === 'assistant'
                        && entry.isGreeting !== true
                        && entry.isTyping !== true
                        && entry.isReasoningPreview !== true
                        && !String(entry.text || '').trim()
                    );
                    const showUserRetry = transcriptEndsOnUserPath(messageRecord.path)
                        && !turnBusy
                        && !emptyAssistantRetry;
                    userRetryButton.hidden = !showUserRetry;
                    userRetryButton.disabled = !showUserRetry;
                    return;
                }
                if (role !== 'assistant') return;
                const greetingMeta = HangoutState.getInstance().getGreetingVariationMeta();
                const greetingIndex = (Number(greetingMeta.variation) || 0) + 1;
                const greetingTotal = Number(greetingMeta.count) || 1;
                const isGreetingNavHost = messageRecord.isGreeting && isGreetingVariationNavHost(messageRecord);
                const showVersionNav = messageRecord.isGreeting
                    ? isGreetingNavHost && greetingTotal > 1
                    : true;
                const displayIndex = messageRecord.isGreeting ? greetingIndex : (Number(messageRecord.versionIndex) || 1);
                const displayTotal = messageRecord.isGreeting ? greetingTotal : (Number(messageRecord.versionTotal) || 1);
                if (leftArrowButton) leftArrowButton.hidden = !showVersionNav;
                if (rightArrowButton) rightArrowButton.hidden = !showVersionNav;
                if (versionCountEl) {
                    versionCountEl.hidden = !showVersionNav || displayTotal <= 1;
                    versionCountEl.textContent = `${displayIndex}/${displayTotal}`;
                }
                const greetingNavBusy = messageRecord.isTyping || messageRecord.isEditing;
                const regularNavBusy = greetingNavBusy || messageRecord.isAwaitingDecision;
                if (leftArrowButton) {
                    const isDisabled = (messageRecord.isGreeting ? greetingNavBusy : regularNavBusy)
                        || displayIndex <= 1;
                    leftArrowButton.disabled = isDisabled;
                    leftArrowButton.setAttribute('aria-disabled', isDisabled ? 'true' : 'false');
                }
                if (rightArrowButton) {
                    const isDisabled = (messageRecord.isGreeting ? greetingNavBusy : regularNavBusy)
                        || (messageRecord.isGreeting && displayIndex >= displayTotal);
                    rightArrowButton.disabled = isDisabled;
                    rightArrowButton.setAttribute('aria-disabled', isDisabled ? 'true' : 'false');
                }
                if (continueButton) {
                    const hangoutState = HangoutState.getInstance();
                    const visible = getTranscriptForLayout(hangoutState);
                    const lastAssistant = [...visible].reverse().find((entry) =>
                        entry?.role === 'assistant' && !isGreetingMessage(entry)
                    );
                    const recordPath = normalizeMessagePath(messageRecord.path);
                    const lastPath = normalizeMessagePath(lastAssistant?.path);
                    const isContinueTarget = !messageRecord.isGreeting
                        && Boolean(recordPath)
                        && recordPath === lastPath
                        && String(messageRecord.characterId) === String(lastAssistant?.characterId ?? lastAssistant?.character_id ?? '');
                    const isContinueDisabled = messageRecord.isTyping
                        || messageRecord.isAwaitingDecision
                        || messageRecord.isEditing
                        || !isContinueTarget;
                    continueButton.hidden = !isContinueTarget;
                    continueButton.disabled = isContinueDisabled;
                    continueButton.setAttribute('aria-disabled', isContinueDisabled ? 'true' : 'false');
                }
            },
            beginEdit: () => {
                if (messageRecord.isTyping || messageRecord.isAwaitingDecision || messageRecord.isEditing || messageRecord.isReasoningPreview) return;
                messageRecord.isEditing = true;
                textEl.contentEditable = 'true';
                textEl.classList.add('chatbot-message-text-editing');
                textEl.textContent = messageRecord.text || '';
                textEl.focus();
                messageRecord._renderVersionControls();
            },
            cancelEdit: () => {
                if (!messageRecord.isEditing) return;
                messageRecord.isEditing = false;
                textEl.contentEditable = 'false';
                textEl.classList.remove('chatbot-message-text-editing');
                messageRecord.setText(messageRecord.text || '');
                messageRecord._renderVersionControls();
            },
            commitEdit: async () => {
                if (!messageRecord.isEditing) return;
                const previousText = messageRecord.text || '';
                const nextContent = String(textEl.textContent || '').trim();
                messageRecord.isEditing = false;
                textEl.contentEditable = 'false';
                textEl.classList.remove('chatbot-message-text-editing');
                if (!nextContent) {
                    messageRecord.setText(previousText);
                    messageRecord._renderVersionControls();
                    return;
                }
                if (role === 'assistant' && previousText !== nextContent) {
                    await syncMessageAudioCacheAfterEdit({
                        characterId: messageRecord.characterId,
                        previousText,
                        nextText: nextContent,
                        bubble: messageRecord
                    });
                }
                messageRecord.setText(nextContent);
                messageRecord._renderVersionControls();
                if (!messageRecord.path) return;
                const { saveAssistantMessageEdit } = await loadMessageTreeActions();
                await saveAssistantMessageEdit({
                    path: messageRecord.path,
                    characterId: messageRecord.characterId,
                    content: nextContent,
                    isGreeting: role === 'assistant' && messageRecord.isGreeting === true
                });
            }
        };

        editButton?.addEventListener('mousedown', (event) => {
            event.preventDefault();
        });

        editButton?.addEventListener('click', () => {
            if (messageRecord.isTyping || messageRecord.isAwaitingDecision) return;
            if (messageRecord.isEditing) {
                void messageRecord.commitEdit();
                return;
            }
            messageRecord.beginEdit();
        });

        emptyRetryButton.addEventListener('click', () => {
            if (emptyRetryButton.disabled || isMessageSendInFlight()) return;
            const userPath = resolveRetryUserPath(messageRecord);
            if (!userPath) return;
            void loadMessageTreeActions().then(({ resendUserTurn }) => resendUserTurn(userPath));
        });

        userRetryButton?.addEventListener('click', () => {
            if (userRetryButton.disabled || isMessageSendInFlight()) return;
            const userPath = normalizeMessagePath(messageRecord.path);
            if (!userPath) return;
            void loadMessageTreeActions().then(({ resendUserTurn }) => resendUserTurn(userPath));
        });

        textEl.addEventListener('keydown', (event) => {
            if (!messageRecord.isEditing) return;
            if (event.key === 'Escape') {
                event.preventDefault();
                messageRecord.cancelEdit();
                return;
            }
            if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                void messageRecord.commitEdit();
            }
        });

        textEl.addEventListener('blur', (event) => {
            if (!messageRecord.isEditing) return;
            if (event.relatedTarget === editButton) return;
            void messageRecord.commitEdit();
        });

        if (role === 'assistant') {
            voiceButton.addEventListener('click', () => {
                if (messageRecord.isTyping || messageRecord.isEditing || messageRecord.isReasoningPreview) return;
                void handleVoiceButtonClick(/** @type {any} */ (messageRecord));
            });

            leftArrowButton?.addEventListener('click', () => {
                if (messageRecord.isTyping || messageRecord.isEditing) return;
                if (messageRecord.isGreeting) {
                    void loadMessageTreeActions().then(({ switchGreetingVariation }) =>
                        switchGreetingVariation('left')
                    );
                    return;
                }
                if (messageRecord.isAwaitingDecision) return;
                const resolvedPath = this.resolvePathForMessageRecord(messageRecord);
                if (!resolvedPath) return;
                if (!messageRecord.path) {
                    messageRecord.path = resolvedPath;
                    if (messageRecord.element) messageRecord.element.dataset.path = resolvedPath;
                }
                void loadMessageTreeActions().then(({ navigateAssistantVersion }) =>
                    navigateAssistantVersion(resolvedPath, messageRecord.characterId, 'left')
                );
            });

            rightArrowButton?.addEventListener('click', () => {
                if (messageRecord.isTyping || messageRecord.isEditing) return;
                if (messageRecord.isGreeting) {
                    void loadMessageTreeActions().then(({ switchGreetingVariation }) =>
                        switchGreetingVariation('right')
                    );
                    return;
                }
                if (messageRecord.isAwaitingDecision) return;
                const resolvedPath = this.resolvePathForMessageRecord(messageRecord);
                if (!resolvedPath) return;
                if (!messageRecord.path) {
                    messageRecord.path = resolvedPath;
                    if (messageRecord.element) messageRecord.element.dataset.path = resolvedPath;
                }
                void loadMessageTreeActions().then(({ navigateAssistantVersion }) =>
                    navigateAssistantVersion(resolvedPath, messageRecord.characterId, 'right')
                );
            });

            continueButton?.addEventListener('click', () => {
                if (messageRecord.isTyping || messageRecord.isAwaitingDecision || messageRecord.isEditing) return;
                const resolvedPath = this.resolvePathForMessageRecord(messageRecord);
                if (!resolvedPath) return;
                if (!messageRecord.path) {
                    messageRecord.path = resolvedPath;
                    if (messageRecord.element) messageRecord.element.dataset.path = resolvedPath;
                }
                void loadMessageTreeActions().then(({ continueAssistantMessage }) =>
                    continueAssistantMessage({ path: resolvedPath, characterId: messageRecord.characterId })
                );
            });

        }

        messageRecord._renderVersionControls();

        return messageRecord;
    }

    _extractActionId(value) {
        if (value === null || value === undefined) return null;
        if (typeof value === 'number' && Number.isFinite(value)) return value;
        if (typeof value === 'string') {
            const trimmed = value.trim();
            if (!trimmed) return null;
            const numeric = Number.parseInt(trimmed, 10);
            if (!Number.isNaN(numeric)) return numeric;
        }
        if (typeof value === 'object') {
            const actionCandidate = value.actionId ?? value.action_id ?? value.action ?? null;
            return this._extractActionId(actionCandidate);
        }
        return null;
    }

    _extractOutfitId(value) {
        if (value === null || value === undefined) return 0;
        if (typeof value === 'number' && Number.isFinite(value)) return value;
        if (typeof value === 'string') {
            const trimmed = value.trim();
            if (!trimmed) return 0;
            const numeric = Number.parseInt(trimmed, 10);
            if (!Number.isNaN(numeric)) return numeric;
        }
        return 0;
    }

    _buildSpriteMetaFromValue(spriteState, characterId) {
        const source = spriteState && typeof spriteState === 'object' ? spriteState : {};
        return {
            characterId: String(source.characterId ?? characterId ?? ''),
            actionId: this._extractActionId(source.actionId ?? spriteState),
            outfitId: this._extractOutfitId(source.outfitId),
            isVideo: source.isVideo === true,
            actionName: source.actionName || ''
        };
    }

    _buildAvatarLookupKey(spriteMeta) {
        const characterId = String(spriteMeta?.characterId ?? '');
        const actionId = this._extractActionId(spriteMeta?.actionId);
        if (!characterId || actionId === null) return null;
        const outfitId = this._extractOutfitId(spriteMeta?.outfitId);
        return `${characterId}:${actionId}:${outfitId}`;
    }

    async _resolveCharacterMessageAvatar(spriteMeta, preloadedAvatars = null, options = {}) {
        const characterId = String(spriteMeta?.characterId ?? '');
        if (!characterId) return null;
        const actionId = this._extractActionId(spriteMeta?.actionId);
        if (actionId === null) return null;
        const outfitId = this._extractOutfitId(spriteMeta?.outfitId);
        const avatarLookupKey = this._buildAvatarLookupKey(spriteMeta);
        if (avatarLookupKey && preloadedAvatars?.has(avatarLookupKey)) {
            return preloadedAvatars.get(avatarLookupKey) || null;
        }
        try {
            const spriteUrl = await fetchCharacterSpriteUrlStrict(
                characterId,
                actionId,
                outfitId,
                false,
                { isFullSize: options.isFullSize !== false }
            );
            if (typeof spriteUrl === 'string' && spriteUrl.trim()) return spriteUrl;
        } catch {
            // Fallback to configured avatar/image when sprite fetch fails.
        }
        return null;
    }

    async _preloadMessageAvatars(messages = []) {
        const avatarLookup = new Map();
        const avatarTasks = [];
        const knownKeys = new Set();

        for (const message of messages) {
            if (!message) continue;
            const isRosterNotice = isRosterNoticeMessage(message);
            if (message.role !== 'assistant' && !(message.role === 'system' && isRosterNotice)) continue;
            const characterId = message.characterId ?? message.character_id;
            if (!characterId) continue;
            const resolvedSpriteData = resolveMessageSpriteData(message, {
                characterId,
                fallbackOutfitId: message.outfitId ?? message.outfit_id ?? 0
            });
            const spriteMeta = this._buildSpriteMetaFromValue({
                characterId,
                actionId: resolveChatbotAvatarActionId(message, resolvedSpriteData.actionId),
                outfitId: resolvedSpriteData.outfitId,
                sprite_state: message.sprite_state,
                isVideo: resolvedSpriteData.isVideo,
                actionName: resolvedSpriteData.actionName
            }, characterId);
            const avatarLookupKey = this._buildAvatarLookupKey(spriteMeta);
            if (!avatarLookupKey || knownKeys.has(avatarLookupKey)) continue;
            knownKeys.add(avatarLookupKey);
            avatarTasks.push((async () => {
                const avatarUrl = await this._resolveCharacterMessageAvatar(spriteMeta, null, { isFullSize: false });
                if (avatarUrl) avatarLookup.set(avatarLookupKey, avatarUrl);
            })());
        }

        await Promise.allSettled(avatarTasks);
        return avatarLookup;
    }

    _setAvatarMedia(messageRecord, url, isVideo) {
        const current = messageRecord.avatarElement;
        const desiredTag = isVideo ? 'video' : 'img';
        let el = current;
        if (!current || current.tagName.toLowerCase() !== desiredTag) {
            el = document.createElement(desiredTag);
            el.className = 'chatbot-message-avatar';
            const label = (current && (current.getAttribute('aria-label') || current.alt)) || `${messageRecord.characterName || 'Character'} avatar`;
            if (isVideo) {
                el.muted = true;
                el.loop = true;
                el.autoplay = true;
                el.playsInline = true;
                el.setAttribute('preload', 'metadata');
                el.setAttribute('aria-label', label);
                el.setAttribute('disablepictureinpicture', '');
                el.onerror = () => {
                    const character = this.characterMetaMap.get(String(messageRecord.characterId));
                    const fallbackAvatar = resolveAvatarUrl(character || {});
                    this._setAvatarMedia(messageRecord, isValidAvatarUrl(fallbackAvatar) ? fallbackAvatar : UNKNOWN_AVATAR_URL, false);
                };
            } else {
                el.alt = label;
                el.loading = 'lazy';
            }
            if (current) current.replaceWith(el); else messageRecord.element.prepend(el);
            messageRecord.avatarElement = el;
        }
        if (messageRecord.role === 'assistant' || messageRecord.role === 'system') {
            this._prepareAssistantAvatarInteractive(el, messageRecord.characterName);
        }
        if (el.getAttribute('src') !== url) el.src = url;
        if (isVideo) el.play?.().catch(() => {});
    }

    async _applyAvatar(messageRecord, preloadedAvatars = null) {
        if (messageRecord.role === 'user') {
            this._setAvatarMedia(messageRecord, resolveUserAvatarUrl(), false);
            return;
        }

        const spriteAvatar = await this._resolveCharacterMessageAvatar(messageRecord.spriteMeta, preloadedAvatars);
        if (spriteAvatar) {
            this._setAvatarMedia(messageRecord, spriteAvatar, isVideoAvatarUrl(spriteAvatar));
            return;
        }

        if (isValidAvatarUrl(messageRecord.avatarFallbackUrl)) {
            this._setAvatarMedia(messageRecord, messageRecord.avatarFallbackUrl, false);
            return;
        }

        const character = this.characterMetaMap.get(String(messageRecord.characterId));
        const fallbackAvatar = resolveAvatarUrl(character || {});
        this._setAvatarMedia(messageRecord, isValidAvatarUrl(fallbackAvatar) ? fallbackAvatar : UNKNOWN_AVATAR_URL, false);
    }

    _scrollToBottom() {
        if (!this.messagesContainer) return;
        this.messagesContainer.scrollTop = this.messagesContainer.scrollHeight;
    }

    _appendScenarioHeader(parent = this.messagesContainer) {
        if (!parent) return;
        const { scenarioName, scenarioDescription } = resolveScenarioHeader();
        if (!scenarioName && !scenarioDescription) return;

        const wrapper = document.createElement('section');
        wrapper.className = 'chatbot-transcript-scenario';

        if (scenarioName) {
            const titleEl = document.createElement('h2');
            titleEl.className = 'chatbot-transcript-scenario-title';
            titleEl.textContent = scenarioName;
            wrapper.appendChild(titleEl);
        }

        if (scenarioDescription) {
            const descriptionEl = document.createElement('p');
            descriptionEl.className = 'chatbot-transcript-scenario-description';
            descriptionEl.textContent = scenarioDescription;
            wrapper.appendChild(descriptionEl);
        }

        parent.appendChild(wrapper);
    }

    async createMessage(messageText, characterName, characterId, spriteState = null, options = {}) {
        if (!this.isAvailable()) return null;

        const spriteMeta = this._buildSpriteMetaFromValue(spriteState, characterId);
        const resolvedCharacterName = this._resolveAssistantCharacterName(characterId, [characterName]);
        const streamGenerationId = options.streamGenerationId || null;
        let message = streamGenerationId
            ? this._findMessageByStreamGeneration(characterId, streamGenerationId)
            : this._findLatestTypingMessageByCharacterId(characterId);
        if (message && streamGenerationId
            && message.streamGenerationId
            && String(message.streamGenerationId) !== String(streamGenerationId)) {
            message = null;
        }
        // Deferred typed turns may skip early typing indicators; create on response_start.
        if (!message) {
            message = this._buildMessageRecord(characterId, resolvedCharacterName, {
                role: 'assistant',
                spriteMeta,
                streamGenerationId,
                isGreeting: options.isGreeting === true
            });
            this.activeMessages.push(message);
        } else {
            message.spriteMeta = { ...(message.spriteMeta || {}), ...spriteMeta };
            if (streamGenerationId) message.streamGenerationId = streamGenerationId;
        }

        await this._applyAvatar(message);
        message.isTyping = false;
        message.isAwaitingDecision = true;
        message.characterName = resolvedCharacterName || message.characterName;
        const nameElement = message.element.querySelector('.chatbot-message-name');
        if (nameElement) nameElement.textContent = message.characterName;
        message.setText(messageText || '');
        message.markDirty();
        this._scrollToBottom();
        this.showCanvas();
        return message;
    }

    async createRosterNotice(message = {}) {
        if (!this.isAvailable()) return null;
        const characterId = message.characterId ?? message.character_id;
        if (!characterId || !message.content) return null;

        const resolvedCharacterName = this._resolveAssistantCharacterName(characterId, [
            message.characterName,
            message.character_name
        ]);
        const resolvedSpriteData = resolveMessageSpriteData(message, {
            characterId,
            fallbackOutfitId: message.outfitId ?? message.outfit_id ?? 0
        });
        const spriteMeta = this._buildSpriteMetaFromValue({
            characterId,
            actionId: resolveChatbotAvatarActionId(message, resolvedSpriteData.actionId),
            outfitId: resolvedSpriteData.outfitId,
            sprite_state: message.sprite_state,
            isVideo: resolvedSpriteData.isVideo,
            actionName: resolvedSpriteData.actionName
        }, characterId);

        const record = this._buildMessageRecord(characterId, resolvedCharacterName, {
            role: 'system',
            isRosterNotice: true,
            spriteMeta,
            avatarFallbackUrl: resolveAvatarUrl(message || {}),
            path: normalizeMessagePath(message.path) || null
        });
        record.setText(message.content || '');
        await this._applyAvatar(record);
        this.activeMessages.push(record);
        this._scrollToBottom();
        this.showCanvas();
        return record;
    }

    async createUserMessage(messageText, options = {}) {
        if (!this.isAvailable()) return null;

        const path = normalizeMessagePath(options?.path) || null;
        if (path) {
            const existing = this.activeMessages.find((message) =>
                message?.role === 'user'
                && normalizeMessagePath(message.path) === path
            );
            if (existing) {
                existing.setText(messageText || '');
                this._scrollToBottom();
                this.showCanvas();
                return existing;
            }
        }

        const userRecord = this._buildMessageRecord('user', resolveUserDisplayName(), {
            role: 'user',
            path
        });
        await this._applyAvatar(userRecord);
        userRecord.setText(messageText || '');
        this.activeMessages.push(userRecord);
        this._scrollToBottom();
        this.showCanvas();
        return userRecord;
    }

    async createTypingIndicator(characterId, characterName, options = {}) {
        if (!this.isAvailable()) return null;

        const streamGenerationId = options?.streamGenerationId ? String(options.streamGenerationId) : null;
        const existingByGeneration = streamGenerationId
            ? this._findMessageByStreamGeneration(characterId, streamGenerationId)
            : null;
        if (existingByGeneration) return existingByGeneration;

        const existingTyping = streamGenerationId
            ? null
            : this._findLatestTypingMessageByCharacterId(characterId);
        if (existingTyping) return existingTyping;

        const replacePath = normalizeMessagePath(options?.path);
        const existingByPath = replacePath
            ? this._findMessageRecordByPath(replacePath, characterId)
            : null;
        const pathGeneration = existingByPath?.streamGenerationId ? String(existingByPath.streamGenerationId) : '';
        const pathGenerationMismatch = Boolean(
            existingByPath
            && pathGeneration
            && streamGenerationId
            && pathGeneration !== streamGenerationId
        );
        if (existingByPath && !pathGenerationMismatch) {
            if (streamGenerationId) existingByPath.streamGenerationId = streamGenerationId;
            const resolvedCharacterName = this._resolveAssistantCharacterName(characterId, [
                characterName,
                existingByPath.characterName
            ]);
            existingByPath.characterName = resolvedCharacterName || existingByPath.characterName;
            const nameElement = existingByPath.element?.querySelector?.('.chatbot-message-name');
            if (nameElement && resolvedCharacterName) nameElement.textContent = resolvedCharacterName;
            existingByPath.isTyping = true;
            existingByPath.isAwaitingDecision = true;
            existingByPath.setText('...');
            existingByPath._renderVersionControls?.();
            this._scrollToBottom();
            this.showCanvas();
            return existingByPath;
        }

        const resolvedCharacterName = this._resolveAssistantCharacterName(characterId, [characterName]);

        const message = this._buildMessageRecord(characterId, resolvedCharacterName, {
            role: 'assistant',
            spriteMeta: { characterId: String(characterId), actionId: null, outfitId: 0 },
            streamGenerationId,
            isGreeting: options.isGreeting === true
        });
        message.characterName = resolvedCharacterName;
        const nameElement = message.element.querySelector('.chatbot-message-name');
        if (nameElement) nameElement.textContent = resolvedCharacterName;
        message.isTyping = true;
        message.isAwaitingDecision = true;
        await this._applyAvatar(message);
        this.activeMessages.push(message);
        message.setText('...');
        this._scrollToBottom();
        this.showCanvas();
        return message;
    }

    async removeTypingIndicator(characterId) {
        const typingMessage = this._findLatestTypingMessageByCharacterId(characterId);
        if (!typingMessage) return;
        typingMessage.isTyping = false;
        if ((typingMessage.text || '').trim() === '...') {
            typingMessage.setText('');
        }
    }

    updateTextboxText(characterId, newText, options = {}) {
        const targetMessage = this._resolveStreamTargetMessage(characterId, options);
        if (!targetMessage) return;
        const generationId = options.streamGenerationId ? String(options.streamGenerationId) : '';
        if (generationId && String(targetMessage.streamGenerationId || '') !== generationId) return;
        // While awaiting post-text decisions, keep isTyping under stream control
        // (cleared on text_complete) so voice unlocks separately from version arrows.
        if (!targetMessage.isAwaitingDecision) {
            targetMessage.isTyping = false;
        }
        targetMessage.setText(newText || '');
        this._scrollToBottom();
    }

    findTextboxByCharacterId(characterId, options = {}) {
        return this._resolveStreamTargetMessage(characterId, options);
    }

    getActiveTextboxes() {
        return [...this.activeMessages];
    }

    getActiveMessages() {
        return this.getActiveTextboxes();
    }

    async refreshMessageAvatar(characterId, spriteState = null, options = {}) {
        const message = this._resolveStreamTargetMessage(characterId, options)
            || this._findLatestMessageByCharacterId(characterId);
        if (!message || message.role === 'user') return null;
        if (spriteState) {
            const spriteMeta = this._buildSpriteMetaFromValue(spriteState, characterId);
            message.spriteMeta = { ...(message.spriteMeta || {}), ...spriteMeta };
        }
        await this._applyAvatar(message);
        message.markDirty?.();
        return message;
    }

    async refreshMessageAvatarByPath(path, characterId, spriteState = null) {
        const message = this._findMessageRecordByPath(path, characterId);
        if (!message || message.role === 'user') return null;
        if (spriteState) {
            const spriteMeta = this._buildSpriteMetaFromValue(spriteState, characterId);
            message.spriteMeta = { ...(message.spriteMeta || {}), ...spriteMeta };
        }
        await this._applyAvatar(message);
        message.markDirty?.();
        return message;
    }

    setActiveTextboxes(textboxes) {
        this.activeMessages = Array.isArray(textboxes) ? [...textboxes] : [];
        if (!this.messagesContainer) return;
        this.messagesContainer.innerHTML = '';
        for (const message of this.activeMessages) {
            if (message?.element) this.messagesContainer.appendChild(message.element);
        }
        this._scrollToBottom();
    }

    setActiveMessages(messages) {
        this.setActiveTextboxes(messages);
    }

    clearActiveTextboxes() {
        this._hydratedSignature = null;
        this.activeMessages = [];
        if (this.messagesContainer) this.messagesContainer.innerHTML = '';
    }

    clearActiveMessages() {
        this.clearActiveTextboxes();
    }

    clearAllTextboxes() {
        this.clearActiveTextboxes();
    }

    clearAllMessages() {
        this.clearAllTextboxes();
    }

    hasActiveTextboxes() {
        return this.activeMessages.length > 0;
    }

    hasActiveMessages() {
        return this.hasActiveTextboxes();
    }

    async getCharacterDisplayIndex(characterId) {
        const message = this._findLatestMessageByCharacterId(characterId);
        if (message) return this.activeMessages.indexOf(message);
        this.lastDisplayIndex += 1;
        return this.lastDisplayIndex;
    }

    redrawAllMessages() {
        for (const message of this.activeMessages) {
            message.markDirty?.();
        }
    }

    async saveAllBubblePositions() {
        return;
    }

    showCanvas() {
        if (this.container) this.container.classList.remove('hidden');
    }

    hideCanvas() {
        if (this.container) this.container.classList.add('hidden');
    }

    _applyAvatarFromPreload(messageRecord, preloadedAvatars = null) {
        if (messageRecord.role === 'user') {
            this._setAvatarMedia(messageRecord, resolveUserAvatarUrl(), false);
            return;
        }

        const avatarLookupKey = this._buildAvatarLookupKey(messageRecord.spriteMeta);
        const spriteAvatar = avatarLookupKey && preloadedAvatars?.get(avatarLookupKey);
        if (spriteAvatar) {
            this._setAvatarMedia(messageRecord, spriteAvatar, false);
            return;
        }

        if (isValidAvatarUrl(messageRecord.avatarFallbackUrl)) {
            this._setAvatarMedia(messageRecord, messageRecord.avatarFallbackUrl, false);
            return;
        }

        const character = this.characterMetaMap.get(String(messageRecord.characterId));
        const fallbackAvatar = resolveAvatarUrl(character || {});
        this._setAvatarMedia(messageRecord, isValidAvatarUrl(fallbackAvatar) ? fallbackAvatar : UNKNOWN_AVATAR_URL, false);
    }

    _computeHydrateSignature(messages = []) {
        const hangoutState = HangoutState.getInstance();
        const { variation } = hangoutState.getGreetingVariationMeta();
        return `${variation}|${(Array.isArray(messages) ? messages : [])
            .map((message) => `${normalizeMessagePath(message?.path)}:${message?.role}:${message?.characterId ?? message?.character_id ?? ''}:${String(message?.content ?? '')}`)
            .join('|')}`;
    }

    async upgradeChatAvatarsToVideo() {
        if (!this.isAvailable()) return;
        const uniqueSpriteMeta = new Map();
        for (const record of this.activeMessages) {
            if (record?.role !== 'assistant' && record?.role !== 'system') continue;
            const key = this._buildAvatarLookupKey(record.spriteMeta);
            if (!key || uniqueSpriteMeta.has(key)) continue;
            uniqueSpriteMeta.set(key, record.spriteMeta);
        }

        const videoUrls = new Map();
        await Promise.allSettled([...uniqueSpriteMeta.entries()].map(async ([key, spriteMeta]) => {
            try {
                const url = await fetchCharacterSpriteUrlStrict(
                    spriteMeta.characterId,
                    spriteMeta.actionId,
                    spriteMeta.outfitId,
                    false,
                    { isFullSize: true }
                );
                if (url && isVideoAvatarUrl(url)) videoUrls.set(key, url);
            } catch {
                // Keep the static thumbnail when video resolution fails.
            }
        }));

        for (const record of this.activeMessages) {
            if (record?.role !== 'assistant' && record?.role !== 'system') continue;
            const key = this._buildAvatarLookupKey(record.spriteMeta);
            const videoUrl = key && videoUrls.get(key);
            if (videoUrl) this._setAvatarMedia(record, videoUrl, true);
        }
    }

    async hydrateFromHistory(messages = []) {
        if (!this.isAvailable()) return;
        const hydrateSignature = this._computeHydrateSignature(messages);
        if (hydrateSignature && hydrateSignature === this._hydratedSignature && this.activeMessages.length > 0) {
            if (!deferChatAvatarVideoUpgrade) {
                void this.upgradeChatAvatarsToVideo();
            }
            return;
        }

        this.characterMetaMap = await resolveCharacterMetaMap();
        this.clearAllMessages();
        const transcriptFragment = document.createDocumentFragment();
        this._appendScenarioHeader(transcriptFragment);
        const hangoutState = HangoutState.getInstance();
        const stateMessages = hangoutState?.getMessages?.();
        const allMessages = Array.isArray(stateMessages) && stateMessages.length > 0 ? stateMessages : messages;
        const activeVersions = hangoutState?.getActiveVersions?.() || {};
        const versionMetaIndex = buildVersionMetaIndex(allMessages, activeVersions);
        const preloadedAvatars = await this._preloadMessageAvatars(messages);

        for (const message of messages) {
            if (!message) continue;
            const hasContent = String(message.content || '').trim().length > 0;
            const isEmptyAssistant = message.role === 'assistant'
                && !hasContent
                && !isGreetingMessage(message);
            if (!hasContent && !isEmptyAssistant) continue;
            const isRosterNotice = isRosterNoticeMessage(message);
            if (message.role !== 'assistant' && message.role !== 'user' && !(message.role === 'system' && isRosterNotice)) continue;

            if (message.role === 'assistant' || isRosterNotice) {
                const characterId = message.characterId ?? message.character_id;
                if (!characterId) continue;
                const resolvedSpriteData = resolveMessageSpriteData(message, {
                    characterId,
                    fallbackOutfitId: message.outfitId ?? message.outfit_id ?? 0
                });
                const spriteMeta = this._buildSpriteMetaFromValue({
                    characterId,
                    actionId: resolveChatbotAvatarActionId(message, resolvedSpriteData.actionId),
                    outfitId: resolvedSpriteData.outfitId,
                    sprite_state: message.sprite_state,
                    isVideo: resolvedSpriteData.isVideo,
                    actionName: resolvedSpriteData.actionName
                }, characterId);
                const isGreeting = !isRosterNotice && isGreetingMessage(message);
                const greetingMeta = hangoutState.getGreetingVariationMeta();
                const versionMeta = isGreeting
                    ? {
                        canonicalPath: normalizeMessagePath(message.path) || null,
                        index: (Number(greetingMeta.variation) || 0) + 1,
                        total: Number(greetingMeta.count) || 1
                    }
                    : getVersionMetaFromIndex(
                        versionMetaIndex,
                        message.path,
                        characterId
                    );
                const record = this._buildMessageRecord(
                    characterId,
                    this._resolveAssistantCharacterName(characterId, [
                        message.characterName,
                        message.character_name
                    ]),
                    {
                        role: isRosterNotice ? 'system' : 'assistant',
                        isRosterNotice,
                        spriteMeta,
                        avatarFallbackUrl: resolveAvatarUrl(message || {}),
                        path: normalizeMessagePath(message.path) || null,
                        canonicalPath: isRosterNotice ? null : (versionMeta.canonicalPath || null),
                        isGreeting,
                        versionIndex: isRosterNotice ? 1 : versionMeta.index,
                        versionTotal: isRosterNotice ? 1 : versionMeta.total,
                        mountTarget: transcriptFragment
                    }
                );
                record.setText(message.content || '');
                if (!isRosterNotice) record._renderVersionControls?.();
                this._applyAvatarFromPreload(record, preloadedAvatars);
                this.activeMessages.push(record);
                continue;
            }

            const userRecord = this._buildMessageRecord('user', resolveUserDisplayName(), {
                role: 'user',
                path: normalizeMessagePath(message.path) || null,
                mountTarget: transcriptFragment
            });
            this._applyAvatarFromPreload(userRecord);
            userRecord.setText(message.content || '');
            userRecord._renderVersionControls?.();
            this.activeMessages.push(userRecord);
        }

        if (this.messagesContainer) {
            this.messagesContainer.appendChild(transcriptFragment);
        }
        this._hydratedSignature = hydrateSignature;
        this.redrawAllMessages();
        this._scrollToBottom();
        if (!deferChatAvatarVideoUpgrade) {
            void this.upgradeChatAvatarsToVideo();
        }
    }

    cleanup() {
        if (this.messagesContainer) {
            if (this._avatarFocusClickHandler) {
                this.messagesContainer.removeEventListener('click', this._avatarFocusClickHandler);
            }
            if (this._avatarFocusKeyHandler) {
                this.messagesContainer.removeEventListener('keydown', this._avatarFocusKeyHandler);
            }
        }
        this._avatarFocusClickHandler = null;
        this._avatarFocusKeyHandler = null;
        this.clearAllMessages();
        this.messagesContainer = null;
        this.container = null;
        this.initialized = false;
    }
}

let chatbotDisplayManagerInstance = null;

export async function getChatbotDisplayManager() {
    if (!chatbotDisplayManagerInstance) {
        chatbotDisplayManagerInstance = new ChatbotDisplayManager();
    }
    if (!chatbotDisplayManagerInstance.initialized) {
        await chatbotDisplayManagerInstance.initialize();
    }
    return chatbotDisplayManagerInstance;
}
